import React, {
    useState, useEffect, useRef, useCallback, useContext,
} from 'react';
import {
    Box, Flex, Text, Textarea, IconButton, Button, Spinner, Tooltip,
    useToast, Badge, Image as ChakraImage, HStack, VStack, Divider,
    Input, Icon, Link as ChakraLink, Alert, AlertIcon, Select, Avatar,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalFooter, ModalCloseButton, useDisclosure, useBreakpointValue,
} from '@chakra-ui/react';
import { IoMdSend } from 'react-icons/io';
import { FaPaperclip, FaTimes, FaFilePdf, FaFileAlt, FaSync, FaDownload, FaEnvelopeOpen, FaEnvelope, FaCheck, FaCheckDouble, FaExclamationTriangle } from 'react-icons/fa';
import {
    api,
    getMensagemVenda,
    sincronizarMensagensVenda,
    enviarMensagemVenda,
    enviarMensagemComAnexo,
    marcarMensagemLida,
    marcarMensagemNaoLida,
} from '../../services/api';
import { AuthContext } from '../../contexts/auth';

const TEMPLATE_LABELS = {
    REQUEST_VARIANTS: 'Solicitar compatibilidade (chassi/modelo/ano)',
    REQUEST_BILLING_INFO: 'Solicitar dados de faturamento',
    SEND_INVOICE_LINK: 'Enviar link da nota fiscal',
    OTHER: 'Mensagem livre',
};

// Texto padrao que o Mercado Livre envia em cada modelo (nao editavel).
const TEMPLATE_TEXTOS = {
    REQUEST_VARIANTS: 'Olá! Por favor, confirme as características que quer para o '
        + 'produto que você comprou, assim podemos fazer o envio.',
    REQUEST_BILLING_INFO: 'Para emitir a nota fiscal, precisamos confirmar os seus '
        + 'dados de faturamento.',
    SEND_INVOICE_LINK: 'Segue o link da nota fiscal da sua compra.',
};

const AnexoPreview = ({ anexo, vendaId }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const isImage = anexo.type?.startsWith('image/') ||
        /\.(jpg|jpeg|png|heic|heif)$/i.test(anexo.original_filename || '');
    const isPdf = anexo.type === 'application/pdf' ||
        /\.pdf$/i.test(anexo.original_filename || '');

    useEffect(() => {
        if (!isImage || !anexo.filename || !vendaId) return;
        let active = true;
        api.get(`/mensageria/anexo/${encodeURIComponent(anexo.filename)}`, {
            params: { venda_id: vendaId },
            responseType: 'blob',
        }).then(res => {
            if (active) setBlobUrl(URL.createObjectURL(res.data));
        }).catch(() => {});
        return () => { active = false; };
    }, [isImage, anexo.filename, vendaId]);

    const handleDownload = async () => {
        try {
            const res = await api.get(`/mensageria/anexo/${encodeURIComponent(anexo.filename)}`, {
                params: { venda_id: vendaId },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = anexo.original_filename || anexo.filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (_) {}
    };

    if (isImage) {
        if (!blobUrl) return <Spinner size="sm" mt={1} />;
        return (
            <Box onClick={() => window.open(blobUrl, '_blank')} cursor="pointer" display="inline-block">
                <ChakraImage
                    src={blobUrl}
                    alt={anexo.original_filename}
                    maxH="120px"
                    maxW="180px"
                    borderRadius="md"
                    objectFit="cover"
                    mt={1}
                    _hover={{ opacity: 0.85 }}
                />
            </Box>
        );
    }

    return (
        <Flex align="center" gap={1} mt={1} color="blue.600" fontSize="12px" cursor="pointer" onClick={handleDownload}>
            {isPdf ? <FaFilePdf /> : <FaFileAlt />}
            <Text textDecor="underline" isTruncated maxW="160px">
                {anexo.original_filename || anexo.filename}
            </Text>
            <FaDownload size={10} />
        </Flex>
    );
};

const BolhaMensagem = ({ msg, vendaId, nomeComprador }) => {
    const isTaga = msg.taga === 1;
    return (
        <Flex
            direction={isTaga ? 'row-reverse' : 'row'}
            align="flex-end"
            gap={2}
            mb={2}
            px={1}
        >
            <Avatar
                size="xs"
                name={isTaga ? 'TAGA' : (nomeComprador || 'Cliente')}
                src={isTaga ? '/user.png' : '/client.webp'}
                flexShrink={0}
            />
            <Flex
                direction="column"
                align={isTaga ? 'flex-end' : 'flex-start'}
                maxW={{ base: '78%', md: '68%' }}
            >
                <Box
                    bg={isTaga ? '#eb9659' : '#f0f0f0'}
                    color="black"
                    px={3}
                    py={2}
                    borderRadius={isTaga
                        ? '16px 16px 4px 16px'
                        : '16px 16px 16px 4px'}
                    boxShadow="sm"
                >
                    {msg.text && (
                        <Text fontSize="13px" whiteSpace="pre-wrap" wordBreak="break-word">
                            {msg.text}
                        </Text>
                    )}
                    {Array.isArray(msg.arquivos) && msg.arquivos.map((a, i) => (
                        <AnexoPreview key={i} anexo={a} vendaId={vendaId} />
                    ))}
                </Box>
                <HStack spacing={1} mt="2px" px={1}>
                    <Text fontSize="10px" color="gray.400">{msg.data}</Text>
                    {isTaga && (
                        msg.status === 'nao_confirmada' ? (
                            // O ML aceitou o envio e depois nao listou a mensagem
                            // no historico: a moderacao derrubou. Sem este aviso o
                            // operador acha que o comprador recebeu.
                            <Tooltip label="O Mercado Livre não confirmou esta mensagem: provavelmente foi barrada pela moderação e o comprador não recebeu" isLazy>
                                <Box as="span" lineHeight="0">
                                    <FaExclamationTriangle color="#DD6B20" size={10} />
                                </Box>
                            </Tooltip>
                        ) : msg.lida_em ? (
                            <Tooltip label="Lida pelo comprador" isLazy>
                                <Box as="span" lineHeight="0">
                                    <FaCheckDouble color="#3182CE" size={10} />
                                </Box>
                            </Tooltip>
                        ) : msg.entregue_em ? (
                            <Tooltip label="Entregue" isLazy>
                                <Box as="span" lineHeight="0">
                                    <FaCheckDouble color="#9CA3AF" size={10} />
                                </Box>
                            </Tooltip>
                        ) : (
                            <Tooltip label="Enviada" isLazy>
                                <Box as="span" lineHeight="0">
                                    <FaCheck color="#9CA3AF" size={10} />
                                </Box>
                            </Tooltip>
                        )
                    )}
                </HStack>
            </Flex>
        </Flex>
    );
};

// Mesmos textos da pilula "Sem resposta possivel" da lista, para a tela nao
// explicar a mesma coisa de duas formas diferentes.
const MOTIVO_BLOQUEIO = {
    venda_cancelada: 'Venda cancelada e sem conversa aberta: o Mercado Livre bloqueia o envio.',
    cancelada_prazo_vencido: 'Venda cancelada: o prazo de 30 dias para responder o comprador venceu.',
    sem_opcao_no_ml: 'O Mercado Livre não oferece nenhuma opção de mensagem para esta venda.',
};

const ChatMensagensML = ({
    idOrigem,
    vendaId,
    packId: packIdExterno,
    isOpen,
    onClose,
    nomeCompradorInicial,
    onMarcouLida,
    onStatusLeituraChange,
}) => {
    const { user } = useContext(AuthContext);
    const toast = useToast({ position: 'top-right', duration: 4000 });
    const msgEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const initialFocusRef = useRef(null);
    const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false });

    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingEnvio, setLoadingEnvio] = useState(false);
    const [texto, setTexto] = useState('');
    const [tipoSelecionado, setTipoSelecionado] = useState('OTHER');
    const [arquivo, setArquivo] = useState(null);
    const [previewArquivo, setPreviewArquivo] = useState(null);
    const [sincronizando, setSincronizando] = useState(false);
    const [lida, setLida] = useState(true);
    const [togglingLeitura, setTogglingLeitura] = useState(false);
    const [templateConfirmar, setTemplateConfirmar] = useState(null);

    const scrollToBottom = useCallback(() => {
        setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    }, []);

    const carregarMensagens = useCallback(async () => {
        if (!idOrigem) return;
        setLoading(true);
        try {
            const res = await getMensagemVenda(idOrigem);
            setDados(res);
            const packId = res?.packId || packIdExterno;
            if (packId) {
                marcarMensagemLida(packId).catch(() => {});
                onMarcouLida?.(packId);
                setLida(true);
            }
            scrollToBottom();
        } catch (e) {
            toast({ title: 'Erro ao carregar mensagens', status: 'error' });
        } finally {
            setLoading(false);
        }
    }, [idOrigem, packIdExterno, scrollToBottom, toast]);

    useEffect(() => {
        if (isOpen && idOrigem) {
            setDados(null);
            setTexto('');
            setArquivo(null);
            setPreviewArquivo(null);
            setTemplateConfirmar(null);
            carregarMensagens();
        }
    }, [isOpen, idOrigem]);

    useEffect(() => {
        if (dados?.mensagens?.length) scrollToBottom();
    }, [dados?.mensagens?.length]);

    const handleArquivo = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 25 * 1024 * 1024) {
            toast({ title: 'Arquivo muito grande (máx. 25 MB)', status: 'warning' });
            return;
        }
        setArquivo(f);
        if (f.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewArquivo(ev.target.result);
            reader.readAsDataURL(f);
        } else {
            setPreviewArquivo(null);
        }
    };

    const removerArquivo = () => {
        setArquivo(null);
        setPreviewArquivo(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleToggleLeitura = async () => {
        const packId = dados?.packId || packIdExterno;
        if (!packId) return;
        setTogglingLeitura(true);
        try {
            if (lida) {
                await marcarMensagemNaoLida(packId);
                setLida(false);
                onStatusLeituraChange?.(packId, true);
            } else {
                await marcarMensagemLida(packId);
                setLida(true);
                onStatusLeituraChange?.(packId, false);
                onMarcouLida?.(packId);
            }
        } catch {
            toast({ title: 'Erro ao alterar status de leitura', status: 'error' });
        } finally {
            setTogglingLeitura(false);
        }
    };

    const handleSincronizar = async () => {
        setSincronizando(true);
        try {
            const res = await sincronizarMensagensVenda(idOrigem);
            setDados(res);
            scrollToBottom();
            toast({ title: 'Mensagens atualizadas', status: 'success', duration: 2000 });
        } catch {
            toast({ title: 'Erro ao sincronizar', status: 'error' });
        } finally {
            setSincronizando(false);
        }
    };

    const handleEnviar = async () => {
        const temTexto = texto.trim().length > 0;
        const temArquivo = arquivo !== null;

        if (!temTexto && !temArquivo) {
            toast({ title: 'Digite uma mensagem ou selecione um arquivo', status: 'warning' });
            return;
        }

        setLoadingEnvio(true);
        try {
            let res;
            if (temArquivo) {
                res = await enviarMensagemComAnexo(idOrigem, texto.trim(), arquivo);
            } else if (tipoSelecionado !== 'OTHER') {
                res = await enviarMensagemVenda(idOrigem, '', tipoSelecionado);
            } else {
                res = await enviarMensagemVenda(idOrigem, texto.trim(), 'OTHER');
            }

            if (res?.sucesso) {
                const novaMensagem = {
                    id: null,
                    text: texto.trim() || (arquivo ? `[Anexo: ${arquivo.name}]` : ''),
                    data: new Date().toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                    }),
                    taga: 1,
                    arquivos: [],
                    status: 'available',
                };

                setDados(prev => ({
                    ...prev,
                    mensagens: [...(prev?.mensagens || []), novaMensagem],
                }));
                setTexto('');
                removerArquivo();
                scrollToBottom();
                toast({ title: 'Mensagem enviada!', status: 'success', duration: 2000 });
            } else {
                const erro = res?.erro || 'Erro ao enviar mensagem';
                toast({ title: erro, status: 'error' });
            }
        } catch (e) {
            const msg = e?.response?.data?.erro || 'Erro ao enviar mensagem';
            toast({ title: msg, status: 'error' });
        } finally {
            setLoadingEnvio(false);
        }
    };

    const enviarTemplate = async (optionId) => {
        setLoadingEnvio(true);
        try {
            const res = await enviarMensagemVenda(idOrigem, '', optionId);
            if (res?.sucesso) {
                toast({ title: 'Modelo enviado!', status: 'success', duration: 2000 });
                setTemplateConfirmar(null);
                await handleSincronizar();
            } else {
                toast({ title: res?.erro || 'Erro ao enviar modelo', status: 'error' });
            }
        } catch {
            toast({ title: 'Erro ao enviar modelo', status: 'error' });
        } finally {
            setLoadingEnvio(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEnviar();
        }
    };

    const nomeComprador = dados?.nomeComprador || nomeCompradorInicial || 'Cliente';
    // Quando o ML nao responde, a tela abre com o historico de antes; o aviso
    // precisa dizer de quando ele e, senao o operador nao sabe o que confiar.
    const horaDoCache = (iso) => {
        if (!iso) return 'antes';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return 'antes';
        return d.toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    };

    const podeResponder = dados?.podeResponder !== undefined ? dados.podeResponder : true;
    const templates = dados?.templatesDisponiveis || [];
    const temTemplates = templates.filter(t => t.option_id !== 'OTHER').length > 0;
    const templateOther = templates.find(t => t.option_id === 'OTHER');

    // O Mercado Livre nao aceita mais mensagem nesta conversa. Vem da mesma
    // avaliacao que a lista usa na pilula "Sem resposta possivel": sem isto o
    // campo abria normal e o operador escrevia para tomar erro no envio.
    const bloqueada = dados?.respondivel === false;
    const motivoBloqueio = MOTIVO_BLOQUEIO[dados?.motivoBloqueio]
        || 'O Mercado Livre não aceita mais mensagem nesta conversa.';

    const podeEnviarTexto = !bloqueada && (podeResponder || !!templateOther);
    const semOpcoesEnvio = !bloqueada && !podeResponder && templates.length === 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? 'full' : 'xl'}
            scrollBehavior="inside"
            initialFocusRef={initialFocusRef}
        >
            <ModalOverlay />
            <ModalContent
                {...(isMobile ? {
                    m: 0,
                    h: '100%',
                    minH: 0,
                    maxH: '100%',
                    borderRadius: 0,
                } : {
                    maxH: '90vh',
                })}
            >
                <ModalHeader pb={2}>
                    <Flex align="center" justify="space-between" pr={8}>
                        <VStack align="flex-start" spacing={0}>
                            <Text fontSize="15px" fontWeight="bold">
                                Conversa com {nomeComprador}
                            </Text>
                            <HStack spacing={2}>
                                {idOrigem && (
                                    <Text fontSize="11px" color="gray.500">
                                        Venda #{idOrigem}
                                    </Text>
                                )}
                                {dados?.fromCache === false && (
                                    <Text fontSize="10px" color="green.500">Sincronizado agora</Text>
                                )}
                                {dados?.fromCache === true && !dados?.desatualizado && (
                                    <Text fontSize="10px" color="gray.400">Do cache local</Text>
                                )}
                                {dados?.desatualizado && (
                                    <Tooltip
                                        isLazy
                                        label={`O Mercado Livre não respondeu agora. Este histórico é de ${horaDoCache(dados.sincronizadoEm)} e pode não ter as mensagens mais novas.`}
                                    >
                                        <HStack spacing={1} color="orange.500">
                                            <FaExclamationTriangle size={9} />
                                            <Text fontSize="10px">Pode estar desatualizado</Text>
                                        </HStack>
                                    </Tooltip>
                                )}
                            </HStack>
                        </VStack>
                        <HStack spacing={1}>
                            <Tooltip isLazy label={lida ? 'Marcar como não lida' : 'Marcar como lida'}>
                                <IconButton
                                    icon={togglingLeitura ? <Spinner size="xs" /> : (lida ? <FaEnvelopeOpen /> : <FaEnvelope />)}
                                    size="sm"
                                    variant="ghost"
                                    colorScheme={lida ? 'gray' : 'orange'}
                                    onClick={handleToggleLeitura}
                                    isDisabled={togglingLeitura || loading || !dados}
                                    aria-label="Alternar leitura"
                                />
                            </Tooltip>
                            <Tooltip isLazy label="Forçar sincronização com ML">
                                <IconButton
                                    icon={sincronizando ? <Spinner size="xs" /> : <FaSync />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={handleSincronizar}
                                    isDisabled={sincronizando || loading}
                                    aria-label="Sincronizar"
                                />
                            </Tooltip>
                        </HStack>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton />
                <Divider />

                <ModalBody
                    px={3}
                    py={2}
                    overflowY="auto"
                    flex="1"
                    minH={0}
                    ref={initialFocusRef}
                    tabIndex={-1}
                    _focus={{ outline: 'none' }}
                >
                    {loading ? (
                        <Flex justify="center" align="center" minH="200px">
                            <VStack spacing={2}>
                                <Spinner size="lg" color="orange.400" />
                                <Text fontSize="13px" color="gray.500">Carregando mensagens...</Text>
                            </VStack>
                        </Flex>
                    ) : !dados?.mensagens?.length ? (
                        <Flex justify="center" align="center" minH="120px">
                            <Text color="gray.400" fontSize="13px">Nenhuma mensagem ainda</Text>
                        </Flex>
                    ) : (
                        <Box py={2}>
                            {dados.mensagens.map((msg, i) => (
                                <BolhaMensagem
                                    key={msg.id || i}
                                    msg={msg}
                                    vendaId={vendaId}
                                    nomeComprador={nomeComprador}
                                />
                            ))}
                            <div ref={msgEndRef} />
                        </Box>
                    )}
                </ModalBody>

                <Divider />

                <ModalFooter
                    px={3}
                    py={2}
                    flexDirection="column"
                    alignItems="stretch"
                    gap={2}
                    flexShrink={0}
                    maxH={{ base: '52dvh', md: 'unset' }}
                    overflowY={{ base: 'auto', md: 'unset' }}
                >
                    {!loading && bloqueada && (
                        <Alert status="error" borderRadius="md" py={2} fontSize="12px">
                            <AlertIcon />
                            {motivoBloqueio}
                        </Alert>
                    )}

                    {!loading && semOpcoesEnvio && (
                        <Alert status="warning" borderRadius="md" py={2} fontSize="12px">
                            <AlertIcon />
                            Você já enviou o limite de mensagens permitido pelo Mercado
                            Livre. Aguarde o comprador responder para enviar uma nova mensagem.
                        </Alert>
                    )}

                    {!loading && !bloqueada && !podeResponder && !semOpcoesEnvio && (
                        <Alert status="info" borderRadius="md" py={2} fontSize="12px">
                            <AlertIcon />
                            O comprador ainda não respondeu. O Mercado Livre permite apenas
                            as opções abaixo até que ele escreva.
                        </Alert>
                    )}

                    {!bloqueada && temTemplates && !templateConfirmar && (
                        <Box>
                            <Text fontSize="11px" color="gray.500" mb={1}>
                                Modelos prontos:
                            </Text>
                            {templates
                                .filter(t => t.option_id !== 'OTHER')
                                .map(t => (
                                    <Button
                                        key={t.option_id}
                                        size="sm"
                                        variant="outline"
                                        colorScheme="orange"
                                        mb={1}
                                        mr={1}
                                        isDisabled={loadingEnvio}
                                        onClick={() => setTemplateConfirmar(t.option_id)}
                                    >
                                        {TEMPLATE_LABELS[t.option_id] || t.option_id}
                                    </Button>
                                ))}
                        </Box>
                    )}

                    {templateConfirmar && (
                        <Box
                            border="1px solid"
                            borderColor="orange.200"
                            bg="orange.50"
                            borderRadius="md"
                            p={3}
                        >
                            <Text fontSize="12px" fontWeight="bold" mb={2}>
                                Confirmar envio do modelo:{' '}
                                {TEMPLATE_LABELS[templateConfirmar] || templateConfirmar}
                            </Text>
                            <Text fontSize="11px" color="gray.500" mb={1}>
                                Mensagem que será enviada ao comprador:
                            </Text>
                            <Box
                                bg="white"
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={2}
                                mb={2}
                            >
                                <Text fontSize="13px" fontStyle="italic" color="gray.700">
                                    “{TEMPLATE_TEXTOS[templateConfirmar]
                                        || 'Mensagem padrão do Mercado Livre para este modelo.'}”
                                </Text>
                            </Box>
                            <Text fontSize="10px" color="gray.400" mb={2}>
                                O texto é definido pelo Mercado Livre e não pode ser editado.
                            </Text>
                            <HStack justify="flex-end" spacing={2}>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setTemplateConfirmar(null)}
                                    isDisabled={loadingEnvio}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    size="sm"
                                    colorScheme="orange"
                                    leftIcon={<IoMdSend />}
                                    onClick={() => enviarTemplate(templateConfirmar)}
                                    isLoading={loadingEnvio}
                                >
                                    Confirmar e enviar
                                </Button>
                            </HStack>
                        </Box>
                    )}

                    {podeEnviarTexto && (
                        <>
                            {arquivo && (
                                <Flex
                                    align="center"
                                    bg="gray.50"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    p={2}
                                    gap={2}
                                >
                                    {previewArquivo ? (
                                        <ChakraImage
                                            src={previewArquivo}
                                            boxSize="40px"
                                            objectFit="cover"
                                            borderRadius="md"
                                        />
                                    ) : (
                                        <Icon as={FaFileAlt} color="gray.500" boxSize={5} />
                                    )}
                                    <Text fontSize="12px" flex={1} isTruncated>
                                        {arquivo.name}
                                    </Text>
                                    <IconButton
                                        icon={<FaTimes />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={removerArquivo}
                                        aria-label="Remover arquivo"
                                    />
                                </Flex>
                            )}

                            <Flex gap={2} align="flex-end">
                                <Flex
                                    flex={1}
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="lg"
                                    overflow="hidden"
                                    direction="column"
                                >
                                    <Textarea
                                        placeholder="Digite sua mensagem... (Enter para enviar)"
                                        value={texto}
                                        onChange={e => setTexto(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        resize="none"
                                        border="none"
                                        _focus={{ boxShadow: 'none' }}
                                        fontSize="13px"
                                        minH="60px"
                                        maxH="120px"
                                        maxLength={350}
                                        isDisabled={loadingEnvio}
                                    />
                                    <Flex
                                        justify="space-between"
                                        align="center"
                                        px={2}
                                        py={1}
                                        borderTop="1px solid"
                                        borderColor="gray.100"
                                    >
                                        <Text fontSize="10px" color="gray.400">
                                            {texto.length}/350
                                        </Text>
                                        <Tooltip label="Anexar arquivo (máx. 25 MB)">
                                            <IconButton
                                                icon={<FaPaperclip />}
                                                size="xs"
                                                variant="ghost"
                                                color="gray.500"
                                                onClick={() => fileInputRef.current?.click()}
                                                isDisabled={loadingEnvio}
                                                aria-label="Anexar arquivo"
                                            />
                                        </Tooltip>
                                        <Input
                                            ref={fileInputRef}
                                            type="file"
                                            display="none"
                                            accept=".jpg,.jpeg,.png,.heic,.heif,.pdf,.txt,.doc,.xls,.xml"
                                            onChange={handleArquivo}
                                        />
                                    </Flex>
                                </Flex>

                                <Tooltip label="Enviar (Enter)">
                                    <IconButton
                                        icon={loadingEnvio ? <Spinner size="sm" /> : <IoMdSend />}
                                        bg="#eb9659"
                                        color="white"
                                        _hover={{ bg: '#d4783a' }}
                                        borderRadius="full"
                                        size="md"
                                        isDisabled={loadingEnvio || (!texto.trim() && !arquivo)}
                                        onClick={handleEnviar}
                                        aria-label="Enviar mensagem"
                                    />
                                </Tooltip>
                            </Flex>
                        </>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ChatMensagensML;
